'use client';

import { useEffect, useRef, useState } from 'react';

import { getBookingScrollContainer, scrollToBookingElement } from '../booking-scroll-container';

import {
  BOOKING_STICKY_HEADER_SELECTOR,
  getServiceCategoryHeaderOffset,
} from '../service-category-header-offset';

const SECTION_GAP = 12;

export function useServiceCategoryNavigation(sectionIds: string[]) {
  const listRef = useRef<HTMLDivElement>(null);
  const navigationRef = useRef<HTMLElement>(null);
  const [activeSectionId, setActiveSectionId] = useState<string>();

  // Synchronize the category navigation with browser scroll/layout changes,
  // including expanded service options and the responsive page header.
  useEffect(() => {
    const list = listRef.current;
    const navigation = navigationRef.current;
    if (!list || !navigation) return;

    const scrollContainer = getBookingScrollContainer(list);
    const scrollTarget = scrollContainer ?? window;

    const sections = Array.from(
      list.querySelectorAll<HTMLElement>('[data-service-category-section]'),
    );
    const links = Array.from(
      navigation.querySelectorAll<HTMLAnchorElement>('a'),
    );
    const headers = Array.from(
      document.querySelectorAll<HTMLElement>(
        `header, nav, [role="banner"], .site-header, ${BOOKING_STICKY_HEADER_SELECTOR}`,
      ),
    ).filter((element) => !list.contains(element));
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frame = 0;
    let previousSectionId: string | undefined;

    const update = () => {
      frame = 0;
      // BookingForm mounts both responsive layouts; only the visible list tracks scroll.
      if (!list.getClientRects().length) return;

      const headerBounds = headers.flatMap((header) => {
        const style = getComputedStyle(header);
        const rect = header.getBoundingClientRect();
        if (
          (style.position === 'fixed' || style.position === 'sticky') &&
          style.visibility !== 'hidden' &&
          rect.height > 0 &&
          rect.bottom > 0
        ) {
          return [rect];
        }
        return [];
      });
      const headerBottom = getServiceCategoryHeaderOffset(headerBounds);

      const containerTop = scrollContainer?.getBoundingClientRect().top ?? 0;
      const visibleTop = Math.max(headerBottom, containerTop);
      const sectionTop = visibleTop + navigation.offsetHeight + SECTION_GAP;
      list.style.setProperty('--service-category-top', `${visibleTop - containerTop}px`);
      list.style.setProperty('--service-category-offset', `${sectionTop}px`);

      let active = sections[0];
      for (const section of sections) {
        if (section.getBoundingClientRect().top > sectionTop + 1) break;
        active = section;
      }

      // A short final section may never reach the sticky bar at the page bottom.
      const scrollTop = scrollContainer?.scrollTop ?? window.scrollY;
      const viewportHeight = scrollContainer?.clientHeight ?? window.innerHeight;
      const scrollHeight =
        scrollContainer?.scrollHeight ?? document.documentElement.scrollHeight;
      const atPageBottom =
        scrollTop > 0 && Math.ceil(scrollTop + viewportHeight) >= scrollHeight;
      if (atPageBottom && list.getBoundingClientRect().top <= sectionTop) {
        active = sections[sections.length - 1];
      }

      if (!active || active.id === previousSectionId) return;
      previousSectionId = active.id;
      setActiveSectionId(active.id);

      const link = links.find(
        (link) => link.getAttribute('href') === `#${active.id}`,
      );
      if (!link) return;

      const linkRect = link.getBoundingClientRect();
      const navRect = navigation.getBoundingClientRect();
      if (linkRect.left < navRect.left || linkRect.right > navRect.right) {
        // Scroll only the horizontal strip. scrollIntoView also moves the page.
        navigation.scrollBy({
          left:
            linkRect.left +
            linkRect.width / 2 -
            navRect.left -
            navRect.width / 2,
          behavior: reducedMotion.matches ? 'instant' : 'smooth',
        });
      }
    };

    const scheduleUpdate = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    const observer = new ResizeObserver(scheduleUpdate);
    [document.body, list, navigation, ...headers, ...sections].forEach(
      (element) => observer.observe(element),
    );
    scrollTarget.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    scheduleUpdate();

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
      scrollTarget.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
    };
  }, [sectionIds]);

  const scrollToSection = (sectionId: string) => {
    const list = listRef.current;
    const section = Array.from(
      list?.querySelectorAll<HTMLElement>('[data-service-category-section]') ??
        [],
    ).find((element) => element.id === sectionId);
    if (!list || !section) return;

    const offset =
      Number.parseFloat(
        list.style.getPropertyValue('--service-category-offset'),
      ) || 0;
    section.focus({ preventScroll: true });
    scrollToBookingElement(section, offset);
  };

  return {
    listRef,
    navigationRef,
    activeSectionId:
      activeSectionId && sectionIds.includes(activeSectionId)
        ? activeSectionId
        : sectionIds[0],
    scrollToSection,
  };
}
